import React, { type ReactNode } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { type FigmaGlyph } from '../../icons/glyph'
import { Icon } from '../../icons/Icon'
import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { additionaltextContract } from './additionaltext.contract'

const VARIANTS = additionaltextContract.props.variant.values

export type AdditionalTextVariant = (typeof VARIANTS)[number]

export type AdditionalTextProps = {
    children: ReactNode
    variant?: AdditionalTextVariant
    icon?: ReactNode
    className?: string
    testID?: string
}

/**
 * The line under a form field: a hint, or the reason the field is wrong.
 *
 * The two variants use two different text styles — P2-Regular for the hint,
 * P2-SemiBold for the error — which design confirmed is deliberate rather than
 * a leftover, so the style is read per variant instead of once for the set.
 *
 * The width Figma draws is a fixed 220px frame. It is emitted because the file
 * says FIXED, and a caller that wants the line to follow its field passes
 * `w-full` through `className` rather than the component guessing.
 */
type ContractCell = {
    container: string
    label: string
    icon: string
    parts: Record<string, string>
    glyphs: Record<string, FigmaGlyph>
}

const variantFor = (variant: AdditionalTextVariant): ContractCell => {
    const key = `variant=${variant}`
    const found = (additionaltextContract.variants as Record<string, ContractCell>)[key]
    if (!found) {
        throw new Error(`aos-ds AdditionalText: the Figma contract has no "${key}"`)
    }
    return found
}

const STYLE_FOR: Record<AdditionalTextVariant, string> = {
    default: additionaltextContract.textStyleIds[0],
    error: additionaltextContract.textStyleIds[1],
}

export const AdditionalText = ({
    children,
    variant = 'default',
    icon,
    className,
    testID,
}: AdditionalTextProps) => {
    const cell = variantFor(variant)
    const { container, label } = cell
    const glyph = cell.glyphs.Vector
    const drawnIcon =
        icon === undefined && glyph ? (
            <View className={cell.parts.Vector}>
                <Icon glyph={glyph} className={cell.icon} />
            </View>
        ) : (
            icon
        )
    return (
        <View
            className={twMerge('flex-row items-center', container, className)}
            testID={testID ?? `additional-text-${variant}`}
        >
            {drawnIcon}
            <Typography
                variant={variantForStyleId(STYLE_FOR[variant])}
                className={label}
                testID='additional-text-label'
            >
                {children}
            </Typography>
        </View>
    )
}

AdditionalText.displayName = 'AdditionalText'

export { VARIANTS as ADDITIONAL_TEXT_VARIANTS }
